import React from 'react';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { renderMultiSectionDigitalClockTimeView } from '@mui/x-date-pickers/timeViewRenderers';
import { InputAdornment, TextField, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const StyledPickerInput = styled(TextField)(({ theme, disabled }) => ({
    '& .MuiOutlinedInput-root': {
        // Match the design-system fields: black border by default/hover, the
        // tenant primary colour only while the field is focused (active).
        borderRadius: '10px',
        '& fieldset': {
            borderColor: disabled ? theme.palette.action.disabled : theme.palette.text.primary,
        },
        '&:hover fieldset': {
            borderColor: disabled ? theme.palette.action.disabled : theme.palette.text.primary,
        },
        '&.Mui-focused fieldset': {
            borderColor: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
        },
    },
    '& .MuiInputBase-input': {
        color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
        cursor: disabled ? 'not-allowed' : 'text',
    },
    '& .MuiInputLabel-root': {
        color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
        '&.Mui-focused': {
            color: disabled ? theme.palette.text.disabled : theme.palette.primary.main,
        },
    },
    '& .MuiIconButton-root': {
        color: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
        pointerEvents: disabled ? 'none' : 'auto',
    },
    // Covers the clock icon in both variants: inside the open-picker button on
    // desktop, and as the bare adornment the mobile variant gets below.
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
    },
}));

interface TimePickerComponentProps {
    value: Dayjs | null;
    onChange?: (time: Dayjs | null) => void;
    label: string;
    disabled?: boolean;
    format?: string;
}

// The query MUI uses to choose the desktop variant. Passed explicitly as
// `desktopModeMediaQuery` below so our own `isDesktop` and MUI's variant
// choice are guaranteed to agree rather than both guessing.
const DESKTOP_MODE_MEDIA_QUERY = '@media (pointer: fine)';

const TimePickerComponent: React.FC<TimePickerComponentProps> = React.memo((props: TimePickerComponentProps) => {
    const handleChange = (value: Dayjs | null) => {
        if (props.onChange) {
            props.onChange(value);
        }
    };

    const isDesktop = useMediaQuery(DESKTOP_MODE_MEDIA_QUERY, { defaultMatches: true });
    const format = props.format || 'HH:mm';

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
                disabled={props.disabled}
                value={props.value}
                onChange={handleChange}
                format={format}
                // Keep the clock in the same cycle as the field. Left to itself
                // the clock follows dayjs' global locale, which the app never
                // switches away from `en` (only `moment.locale('de')` is set),
                // so a 12h AM/PM clock fed the 24h `HH:mm` field. Derived from
                // the format rather than pinned, so a caller passing a 12h
                // format still gets a matching clock.
                ampm={/[aA]/.test(format)}
                label={props.label}
                desktopModeMediaQuery={DESKTOP_MODE_MEDIA_QUERY}
                // Scrollable hour/minute columns in BOTH variants, never the
                // clock face. MUI only defaults to the columns on desktop; the
                // mobile variant renders an analogue dial, which means dragging
                // a hand to hit a minute — the operator at a station holds a
                // laptop on a locker and needs to enter a time, not aim at one.
                // Set here at the shared component rather than per screen, so
                // no caller can end up with the dial again.
                viewRenderers={{
                    hours: renderMultiSectionDigitalClockTimeView,
                    minutes: renderMultiSectionDigitalClockTimeView,
                    seconds: renderMultiSectionDigitalClockTimeView,
                }}
                // Every minute selectable. MUI's default steps the minutes
                // column by 5, which silently makes times like 11:07
                // unreachable — a picker that quietly drops values is worse
                // than the dial it replaces.
                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                // The clock icon goes in via the picker's `openPickerIcon` slot,
                // not a custom `InputProps.endAdornment`: `slotProps.textField`
                // shallow-merges over the `InputProps` the picker built, and our
                // `endAdornment` key clobbered its open-picker IconButton (the
                // picker's `ref` survived, which is why the popup still anchored
                // correctly — it simply could never be opened on desktop).
                slots={{ textField: StyledPickerInput, openPickerIcon: AccessTimeIcon }}
                slotProps={{
                    textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        disabled: props.disabled,
                        InputLabelProps: {
                            sx: {
                                color: props.disabled ? 'text.disabled' : 'text.primary',
                                '&.Mui-focused': {
                                    color: props.disabled ? 'text.disabled' : 'primary.main',
                                },
                            },
                        },
                        // The mobile variant builds no open-picker button, so
                        // `openPickerIcon` is never rendered there and the field
                        // would lose its icon entirely. Supplying the adornment
                        // ourselves is safe here precisely because there is no
                        // button for it to clobber.
                        ...(isDesktop ? {} : {
                            InputProps: {
                                endAdornment: (
                                    <InputAdornment position={'end'}>
                                        <AccessTimeIcon />
                                    </InputAdornment>
                                ),
                            },
                        }),
                    },
                    actionBar: {
                        actions: ['cancel', 'accept'],
                    },
                }}
            />
        </LocalizationProvider>
    );
});

export default TimePickerComponent;
